/**
 * Admin module catalog (ODE-P01-T10) — client-side mirror of the backend
 * dashboard module surface (apps/ode-api/src/admin/dashboard.ts) and the
 * OD-015 permission grants (apps/ode-api/src/rbac/catalog.ts).
 *
 * The mirror exists ONLY for UX (nav visibility, pre-emptive blocked state).
 * Authorization is enforced server-side by the T08 guard — the client mirror
 * never grants anything.
 *
 * Traces to: ODE-PRD-REQ-043..048, ODE-PRD-REQ-077.
 */

export type ModuleId =
  | 'live-position'
  | 'do-status'
  | 'checkpoint-timeline'
  | 'journey-history'
  | 'evidence-history'
  | 'operational-reports'
  | 'uang-jalan'
  | 'communication-trace';

export interface ModuleDefinition {
  readonly id: ModuleId;
  readonly title: string;
  readonly description: string;
  readonly permission: string;
  /** Purpose-bound modules require `x-purpose-code` (REQ-101 shape). */
  readonly requiresPurpose: boolean;
  /** Non-admin role grants (mirror of the backend catalog). */
  readonly roles: readonly string[];
  /**
   * Route of the real data-wired module page, when one exists (ODE-P07-T03/
   * T06). Modules without a real page stay on the guarded `/modules/:id`
   * placeholder. UX only — authorization is unchanged (server-enforced).
   */
  readonly route?: string;
}

export const MODULES: readonly ModuleDefinition[] = [
  {
    id: 'live-position',
    title: 'Live Position',
    description: 'Posisi driver secara langsung / near-real-time (REQ-043).',
    permission: 'tracking.view',
    requiresPurpose: false,
    roles: ['dispatcher', 'supervisor', 'auditor'],
  },
  {
    id: 'do-status',
    title: 'Delivery Order Status',
    description: 'Status Delivery Order (REQ-044).',
    permission: 'dashboard.view',
    requiresPurpose: false,
    roles: ['dispatcher', 'supervisor', 'finance', 'auditor'],
  },
  {
    id: 'checkpoint-timeline',
    title: 'Checkpoint Timeline',
    description: 'Linimasa checkpoint perjalanan (REQ-045).',
    permission: 'checkpoint.view',
    requiresPurpose: false,
    roles: ['dispatcher', 'supervisor', 'auditor'],
  },
  {
    id: 'journey-history',
    title: 'Journey History',
    description: 'Riwayat perjalanan / rute (REQ-046).',
    permission: 'journey.view',
    requiresPurpose: false,
    roles: ['dispatcher', 'supervisor', 'auditor'],
    route: '/journey-history',
  },
  {
    id: 'evidence-history',
    title: 'Evidence History',
    description: 'Riwayat foto / bukti checkpoint (REQ-047).',
    permission: 'evidence.view',
    requiresPurpose: false,
    roles: ['dispatcher', 'supervisor', 'auditor'],
  },
  {
    id: 'operational-reports',
    title: 'Laporan Operasional',
    description:
      'Rekap operasional read-only (REQ-048); ekspor terpisah memakai reports.financial.export dan tetap terkunci oleh REQ-088.',
    permission: 'reports.view',
    requiresPurpose: false,
    roles: ['supervisor', 'finance', 'auditor'],
    route: '/reports',
  },
  {
    id: 'communication-trace',
    title: 'Communication Trace',
    description:
      'Jejak komunikasi driver — ringkasan kontak/panggilan/WhatsApp teramati, indikator cakupan, dan konteks investigasi tanpa asumsi pelanggaran (REQ-099).',
    permission: 'communication_trace.summary.read',
    requiresPurpose: false,
    roles: ['auditor'],
  },
];

export function getModule(id: string): ModuleDefinition | undefined {
  return MODULES.find((module) => module.id === id);
}

/**
 * The Admin Uang Jalan Operational View page (ODE-P07-T08).
 *
 * Deliberately NOT part of [MODULES]: `MODULES` mirrors exactly the six
 * REQ-043..048 dashboard placeholders (asserted by the catalog test). This
 * entry carries the real-data page's own permission (`uang_jalan.view`, OD-015
 * financial read) so `RequirePermission` / `hasPermission` can mirror it for
 * UX. The server-side T08 guard remains the authority.
 */
export const UANG_JALAN_MODULE: ModuleDefinition = {
  id: 'uang-jalan',
  title: 'Uang Jalan',
  description:
    'Tampilan operasional Uang Jalan: status, gerbang REQ-011, dan kontrol duplikat/reversal (REQ-071/072).',
  permission: 'uang_jalan.view',
  requiresPurpose: false,
  roles: ['supervisor', 'finance', 'auditor'],
};

/**
 * Client-side mirror of the OD-015 permission grants for the permissions the
 * admin web actually checks (the six REQ-043..048 module permissions plus the
 * real-data pages). Mirrors `apps/ode-api/src/rbac/catalog.ts`; admin is the
 * OD-015 superuser.
 */
export const PERMISSION_ROLES: Readonly<Record<string, readonly string[]>> = {
  'dashboard.view': ['dispatcher', 'supervisor', 'finance', 'auditor'],
  'tracking.view': ['dispatcher', 'supervisor', 'auditor'],
  'tracking.reconcile': ['supervisor'],
  'delivery_order.manage': ['dispatcher'],
  'checkpoint.view': ['dispatcher', 'supervisor', 'auditor'],
  'evidence.view': ['dispatcher', 'supervisor', 'auditor'],
  'journey.view': ['dispatcher', 'supervisor', 'auditor'],
  'geofence.override': ['supervisor'],
  'geofence.override.view': ['supervisor', 'auditor'],
  'uang_jalan.approve': ['supervisor', 'finance'],
  'uang_jalan.reconcile': ['finance'],
  'uang_jalan.view': ['supervisor', 'finance', 'auditor'],
  'reports.view': ['supervisor', 'finance', 'auditor'],
  'reports.financial.export': ['finance', 'auditor'],
  'communication_trace.view_summary': ['auditor'],
  'communication_trace.view_detail': ['auditor'],
  'communication_trace.search': ['auditor'],
  'communication_trace.export': ['auditor'],
  'audit.view': ['auditor'],
  'users.manage': [],
  'system.config': [],
  'legal_hold.propose': ['auditor'],
};

/**
 * Client-side permission check (UX mirror). Unknown permissions deny even
 * for admin (matching the backend `authorize` unknown_permission deny);
 * `admin` is superuser for every known catalog permission (OD-015).
 */
export function hasPermission(roles: readonly string[], permission: string): boolean {
  const definition = MODULES.find((module) => module.permission === permission);
  const mirrored = Object.prototype.hasOwnProperty.call(PERMISSION_ROLES, permission);
  if (definition === undefined && !mirrored) return false;
  if (roles.includes('admin')) return true;
  const grants = PERMISSION_ROLES[permission] ?? definition?.roles ?? [];
  return grants.some((role) => roles.includes(role));
}

/**
 * OD-015 non-admin role grants for permissions that are NOT dashboard module
 * placeholders (client mirror of apps/ode-api/src/rbac/catalog.ts). Used by the
 * Admin Geofence Exceptions & Overrides surface (ODE-P07-T07): the read surface
 * is `geofence.override.view` (supervisor + auditor) and the override
 * create/revoke actions are `geofence.override` (supervisor-only).
 *
 * UX only — the server-side guard on every endpoint remains the authority.
 */
export const STAFF_PERMISSIONS: Readonly<Record<string, readonly string[]>> = {
  'geofence.override.view': ['supervisor', 'auditor'],
  'geofence.override': ['supervisor'],
};

/**
 * Client-side permission check for non-module OD-015 permissions (UX mirror).
 * Unknown permissions deny even for admin; `admin` is superuser for every
 * known catalog permission.
 */
export function hasStaffPermission(roles: readonly string[], permission: string): boolean {
  const mirror = STAFF_PERMISSIONS[permission];
  if (mirror === undefined) return false;
  if (roles.includes('admin')) return true;
  return mirror.some((role) => roles.includes(role));
}
